// The one honest failure state for a server read. It names WHAT failed (the
// endpoint) and WHY (the server's error or the network error), so an operator
// never mistakes an unreachable server for an empty table or a row of zeros.

export function LoadError({
  what,
  endpoint,
  error,
  testId
}: {
  what: string
  endpoint: string
  error: string
  testId?: string
}): JSX.Element {
  return (
    <div
      role="alert"
      data-testid={testId ?? 'load-error'}
      className="rounded-xl p-4 mb-4 text-sm bg-red-500/10 border border-red-500/30 text-red-200"
    >
      <div className="font-medium">Could not load {what}.</div>
      <div className="text-xs text-red-300/80 mt-1">
        <code className="text-red-200">{endpoint}</code> failed: {error}. Nothing below is from this request.
      </div>
    </div>
  )
}

/** Shown where the server answered but does not report a field yet (an older
 *  server build). Distinct from an error and from a real zero. */
export function NotReported({ what, hint }: { what: string; hint?: string }): JSX.Element {
  return (
    <span className="text-[11px] text-slate-500" title={hint ?? `The server does not report ${what} yet.`}>
      not reported
    </span>
  )
}

/** True when an admin endpoint does not exist on the server this console talks
 *  to. Fastify answers an unknown route with 404 and the generic error "Not
 *  Found"; a handler's own 404 ("User not found.") carries its own message, so
 *  the two are told apart rather than every 404 being read as "not deployed". */
export function isMissingEndpoint(status: number, error: string): boolean {
  if (status === 405 || status === 501) return true
  return status === 404 && /^not found$/i.test(error.trim())
}
